import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import StarOutlineRoundedIcon from "@mui/icons-material/StarOutlineRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import PlaylistRemoveOutlinedIcon from "@mui/icons-material/PlaylistRemoveOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import QrCodeScannerOutlinedIcon from "@mui/icons-material/QrCodeScannerOutlined";
import castleLogo from "../../assets/icons/castle_logo.svg";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { buildAuthRequiredPath } from "../../utils/authRedirect";

/**
 * TOP MODEL LAYOUT TUNING
 *
 * Edit MOBILE / DESKTOP values to tune:
 * - main circle size (coreSize)
 * - orbit size (stageSize)
 * - each node angle on orbit (orbitAngle)
 * - text box position near each node (labelDx / labelDy / labelAlign)
 * - text and label sizes
 */
const MODEL_LAYOUT = {
  compact: {
    sectionHeight: 472,
    stageCenterX: 50,
    stageCenterY: 44.2,
    stageSize: 236,
    coreSize: 146,
    nodeSize: 72,
    logoSize: 30,
    centerTextSize: 10.8,
    labelWidth: 112,
    labelTitleSize: 14.6,
    labelDescSize: 9.2,
    nodes: [
      { step: 0, orbitAngle: -90, labelDx: 0, labelDy: -56, labelAlign: "center" },
      { step: 1, orbitAngle: -18, labelDx: 0, labelDy: -52, labelAlign: "center" },
      { step: 2, orbitAngle: -162, labelDx: 0, labelDy: -52, labelAlign: "center" },
      { step: 3, orbitAngle: 126, labelDx: -18, labelDy: 56, labelAlign: "center" },
      { step: 4, orbitAngle: 54, labelDx: 0, labelDy: 56, labelAlign: "center" },
    ],
  },
  mobile: {
    sectionHeight: 528,
    stageCenterX: 50,
    stageCenterY: 44.4,
    stageSize: 286,
    coreSize: 172,
    nodeSize: 88,
    logoSize: 34,
    centerTextSize: 12.5,
    labelWidth: 128,
    labelTitleSize: 17.2,
    labelDescSize: 10.4,
    nodes: [
      // Order requested: top=1, right=2, left=3, bottom-left=4, bottom-right=5
      { step: 0, orbitAngle: -90, labelDx: 0, labelDy: -64, labelAlign: "center" },
      { step: 1, orbitAngle: -18, labelDx: 0, labelDy: -58, labelAlign: "center" },
      { step: 2, orbitAngle: -162, labelDx: 0, labelDy: -58, labelAlign: "center" },
      { step: 3, orbitAngle: 126, labelDx: -20, labelDy: 62, labelAlign: "center" },
      { step: 4, orbitAngle: 54, labelDx: 0, labelDy: 62, labelAlign: "center" },
    ],
  },
  desktop: {
    sectionHeight: 644,
    stageCenterX: 50,
    stageCenterY: 45.2,
    stageSize: 356,
    coreSize: 232,
    nodeSize: 112,
    logoSize: 44,
    centerTextSize: 18,
    labelWidth: 184,
    labelTitleSize: 23,
    labelDescSize: 13,
    nodes: [
      { step: 0, orbitAngle: -90, labelDx: 0, labelDy: -76, labelAlign: "center" },
      { step: 1, orbitAngle: -18, labelDx: 0, labelDy: -68, labelAlign: "center" },
      { step: 2, orbitAngle: -162, labelDx: 0, labelDy: -68, labelAlign: "center" },
      { step: 3, orbitAngle: 126, labelDx: 0, labelDy: 72, labelAlign: "center" },
      { step: 4, orbitAngle: 54, labelDx: 0, labelDy: 72, labelAlign: "center" },
    ],
  },
};

const CHAIN_LAYOUT = {
  mobile: {
    railX: 86.5,
    textX: 3,
    textWidth: 66,
    top: 74,
    mainSize: 102,
    detailSize: 50,
    mainToDetailGap: 34,
    detailToDetailGap: 30,
    detailToMainGap: 54,
    mainTitleSize: 20,
    mainTextSize: 14,
    detailTitleSize: 15,
    detailTextSize: 12.8,
    blockBottomPad: 110,
    connectorGapPx: 8,
    revealStartRatio: 0.74,
    revealBottomInsetPx: 300,
  },
  desktop: {
    railX: 86.5,
    textX: 3,
    textWidth: 64,
    top: 90,
    mainSize: 122,
    detailSize: 58,
    mainToDetailGap: 42,
    detailToDetailGap: 36,
    detailToMainGap: 66,
    mainTitleSize: 34,
    mainTextSize: 24,
    detailTitleSize: 25,
    detailTextSize: 19,
    blockBottomPad: 140,
    connectorGapPx: 10,
    revealStartRatio: 0.78,
    revealBottomInsetPx: 260,
  },
};

const CLUB_SECTION_LAYOUT = {
  mobile: {
    textLeft: 3,
    visualTop: -28,
    visualRight: -40,
  },
  desktop: {
    textLeft: 3,
    visualTop: -50,
    visualRight: -32,
  },
};

const CHAIN_ANIMATION = {
  mainSpan: 0.075,
  detailSpan: 0.075,
  afterMainGap: 0.012,
  afterDetailGap: 0.012,
};

function stepLabelAlignToClass(align) {
  if (align === "left") return "text-left";
  if (align === "right") return "text-right";
  return "text-center";
}

function stepLabelAlignToTransform(align) {
  if (align === "left") return "translate(0, -50%)";
  if (align === "right") return "translate(-100%, -50%)";
  return "translate(-50%, -50%)";
}

function getOrbitPoint(angleDeg) {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: 50 + 50 * Math.cos(rad),
    y: 50 + 50 * Math.sin(rad),
  };
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function easeOutCubic(x) {
  return 1 - (1 - x) ** 3;
}

function easeOutBack(x) {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * (x - 1) ** 3 + c1 * (x - 1) ** 2;
}

function estimateWrappedLines(text, fontSizePx, availableWidthPx) {
  if (!text || !availableWidthPx || !fontSizePx) return 1;
  const avgCharWidth = fontSizePx * 0.53;
  const charsPerLine = Math.max(12, Math.floor(availableWidthPx / avgCharWidth));
  const normalized = String(text).trim().replace(/\s+/g, " ");
  return Math.max(1, Math.ceil(normalized.length / charsPerLine));
}

function findScrollParent(element) {
  let parent = element?.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    const isScrollable = /(auto|scroll|overlay)/.test(overflowY);
    if (isScrollable && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return window;
}

function IconBubble({ Icon, size = 120 }) {
  return (
    <div
      className="relative rounded-full border border-[#4f80dc]/70 bg-[radial-gradient(circle_at_30%_22%,rgba(83,139,255,0.4),rgba(9,24,56,0.95)_58%)] shadow-[0_0_34px_rgba(69,131,255,0.3),inset_0_16px_28px_rgba(106,157,255,0.2),inset_0_-24px_28px_rgba(0,0,0,0.38)]"
      style={{ width: `${size}px`, height: `${size}px` }}
    >
      <Icon className="absolute left-1/2 top-1/2 h-11 w-11 -translate-x-1/2 -translate-y-1/2 text-[#3f97ff]" />
    </div>
  );
}

const HostGuide = ({ extraBottomSpaceCss, forcedLanguageCode, isGuest = false }) => {
  const { t, i18n } = useTranslation(["events", "communities"]);
  const navigate = useNavigate();
  const location = useLocation();
  const returnToCreatePath = location.state?.returnToCreatePath;
  const returnToCreateState = location.state?.returnToCreateState;
  const isStandaloneGuide = !extraBottomSpaceCss;
  const isDesktop = useIsDesktop();
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 390
  );
  const isCompactPhone = !isDesktop && viewportWidth <= 390;
  const modelLayout = isDesktop ? MODEL_LAYOUT.desktop : isCompactPhone ? MODEL_LAYOUT.compact : MODEL_LAYOUT.mobile;
  const chainLayout = isDesktop ? CHAIN_LAYOUT.desktop : CHAIN_LAYOUT.mobile;
  const clubLayout = isDesktop ? CLUB_SECTION_LAYOUT.desktop : CLUB_SECTION_LAYOUT.mobile;
  const topSectionRef = useRef(null);
  const chainSectionRef = useRef(null);
  const [chainProgress, setChainProgress] = useState(0);
  const [topScrollProgress, setTopScrollProgress] = useState(0);
  const [topSpinProgress, setTopSpinProgress] = useState(0);
  const topDissolve = clamp((topScrollProgress - 0.11) * 2.2, 0, 1);
  const topOrbitRotation = topSpinProgress * 19.8 + chainProgress * 26.4;
  const topLabelWidth = isDesktop
    ? modelLayout.labelWidth
    : Math.min(modelLayout.labelWidth, Math.max(viewportWidth - 66, 98));
  const defaultBottomSpace = isDesktop
    ? "120px"
    : "calc(var(--bottom-nav-height, 72px) + env(safe-area-inset-bottom, 0px) + 120px)";
  const contentBottomSpace = extraBottomSpaceCss
    ? `max(${extraBottomSpaceCss}, ${defaultBottomSpace})`
    : defaultBottomSpace;
  const returnToCreation = useCallback(() => {
    if (!returnToCreatePath) return;
    navigate(returnToCreatePath, { state: returnToCreateState });
  }, [navigate, returnToCreatePath, returnToCreateState]);
  const handleCreateEventCta = useCallback(() => {
    if (returnToCreatePath) {
      returnToCreation();
      return;
    }
    if (isGuest) {
      navigate(buildAuthRequiredPath({ next: "/events/create" }), {
        state: { from: { pathname: "/events/create" } },
      });
      return;
    }
    navigate("/events/create", {
      state: { mode: "create" },
    });
  }, [isGuest, navigate, returnToCreatePath, returnToCreation]);

  useEffect(() => {
    const raw = String(forcedLanguageCode || "").trim().toLowerCase();
    if (!raw) return undefined;
    const normalized = raw === "en" || raw === "sk" || raw === "cs" ? raw : "";
    if (!normalized) return undefined;

    const previousLanguage = i18n.resolvedLanguage || i18n.language || "";
    const currentBase = String(i18n.language || "").toLowerCase().split("-")[0];
    const shouldSwitch = currentBase !== normalized;

    if (shouldSwitch) {
      Promise.resolve(i18n.changeLanguage(normalized)).catch(() => {});
    }

    return () => {
      if (!shouldSwitch || !previousLanguage) return;
      Promise.resolve(i18n.changeLanguage(previousLanguage)).catch(() => {});
    };
  }, [forcedLanguageCode, i18n]);

  const steps = useMemo(
    () => [
      {
        title: t("host_guide_v2.steps.creation", { defaultValue: "Creation" }),
        Icon: EventAvailableOutlinedIcon,
      },
      {
        title: t("host_guide_v2.steps.community", { defaultValue: "Community" }),
        Icon: ForumOutlinedIcon,
      },
      {
        title: t("host_guide_v2.steps.management", { defaultValue: "Management" }),
        Icon: TuneOutlinedIcon,
      },
      {
        title: t("host_guide_v2.steps.monetization", { defaultValue: "Monetization" }),
        Icon: PaymentsOutlinedIcon,
      },
      {
        title: t("host_guide_v2.steps.feedback", { defaultValue: "Feedback" }),
        Icon: StarOutlineRoundedIcon,
      },
    ],
    [t]
  );

  const chainFlow = useMemo(
    () => [
      {
        level: "main",
        Icon: EventAvailableOutlinedIcon,
        title: t("host_guide_v2.flow.create_event.title", { defaultValue: "Create Your Event" }),
        text: t("host_guide_v2.flow.create_event.text", { defaultValue: "Set up your event in one place in just a few minutes — with all the details participants need to understand, trust and join it." }),
      },
      {
        level: "detail",
        Icon: EventAvailableOutlinedIcon,
        title: t("host_guide_v2.flow.event_or_concept.title", { defaultValue: "Event or Concept" }),
        text: t("host_guide_v2.flow.event_or_concept.text", { defaultValue: "Create a full event when you already know the date and time, or start with a concept to test interest and pick the best date through a poll." }),
      },
      {
        level: "detail",
        Icon: VisibilityOutlinedIcon,
        title: t("host_guide_v2.flow.event_page.title", { defaultValue: "Event Page" }),
        text: t("host_guide_v2.flow.event_page.text", { defaultValue: "Every event gets its own page with a unique URL, showing your info in a compact and easy-to-read format." }),
      },
      {
        level: "detail",
        Icon: TuneOutlinedIcon,
        title: t("host_guide_v2.flow.audience_settings.title", { defaultValue: "Audience Settings" }),
        text: t("host_guide_v2.flow.audience_settings.text", { defaultValue: "Define age, gender, language or access rules so the right people can discover and join your event." }),
      },
      {
        level: "detail",
        Icon: PersonAddAlt1OutlinedIcon,
        title: t("host_guide_v2.flow.join_requests.title", { defaultValue: "Join Requests" }),
        text: t("host_guide_v2.flow.join_requests.text", { defaultValue: "Choose instant join or request-based access, review applications, and accept the participants that fit your event best." }),
      },
      {
        level: "detail",
        Icon: GroupAddOutlinedIcon,
        title: t("host_guide_v2.flow.cohosts_collabs.title", { defaultValue: "Co-hosts & Collabs" }),
        text: t("host_guide_v2.flow.cohosts_collabs.text", { defaultValue: "Invite co-hosts and run events together as a team for collaborations and shared formats." }),
      },
      {
        level: "main",
        Icon: ForumOutlinedIcon,
        title: t("host_guide_v2.flow.reach_community.title", { defaultValue: "Reach the Right Community" }),
        text: t("host_guide_v2.flow.reach_community.text", { defaultValue: "Publish your event inside a live community of people who already use VaultGrid to discover activities and attend real-life events." }),
      },
      {
        level: "detail",
        title: t("host_guide_v2.flow.responsible_participation.title", { defaultValue: "Responsible Participation" }),
        Icon: ForumOutlinedIcon,
        text: t("host_guide_v2.flow.responsible_participation.text", { defaultValue: "Participants have their own attendance rating and are encouraged to join only when they really plan to come." }),
      },
      {
        level: "detail",
        Icon: GavelOutlinedIcon,
        title: t("host_guide_v2.flow.community_rules.title", { defaultValue: "Healthy Community Rules" }),
        text: t("host_guide_v2.flow.community_rules.text", { defaultValue: "The platform protects boundaries and does not tolerate harassment, pressure or disrespectful behavior." }),
      },
      {
        level: "detail",
        Icon: ChatBubbleOutlineOutlinedIcon,
        title: t("host_guide_v2.flow.auto_chat.title", { defaultValue: "Automatic Event Chat" }),
        text: t("host_guide_v2.flow.auto_chat.text", { defaultValue: "A dedicated event chat is created automatically so you can coordinate everyone in one place." }),
      },
      {
        level: "main",
        Icon: Groups2OutlinedIcon,
        title: t("host_guide_v2.flow.event_management.title", { defaultValue: "Event Management" }),
        text: t("host_guide_v2.flow.event_management.text", { defaultValue: "Manage participants before the event without manually chasing people across forms and private messages." }),
      },
      {
        level: "detail",
        Icon: TaskAltOutlinedIcon,
        title: t("host_guide_v2.flow.intention_confirmation.title", { defaultValue: "Intention Confirmation" }),
        text: t("host_guide_v2.flow.intention_confirmation.text", { defaultValue: "Before the event, participants are asked to confirm they still plan to attend." }),
      },
      {
        level: "detail",
        Icon: PlaylistRemoveOutlinedIcon,
        title: t("host_guide_v2.flow.list_update.title", { defaultValue: "List Update" }),
        text: t("host_guide_v2.flow.list_update.text", { defaultValue: "If someone does not respond in time, the system can remove them and free places for ready participants." }),
      },
      {
        level: "detail",
        Icon: HourglassTopOutlinedIcon,
        title: t("host_guide_v2.flow.smart_waitlist.title", { defaultValue: "Smart Waitlist" }),
        text: t("host_guide_v2.flow.smart_waitlist.text", { defaultValue: "When your event is full, people can still join the waitlist. If someone gives up their spot, VaultGrid automatically offers it to people on the waitlist." }),
      },
      {
        level: "detail",
        Icon: CampaignOutlinedIcon,
        title: t("host_guide_v2.flow.reminders_required.title", { defaultValue: "Reminders & Required Reactions" }),
        text: t("host_guide_v2.flow.reminders_required.text", { defaultValue: "Send reminders and ask for required reactions to important updates in event chat." }),
      },
      {
        level: "detail",
        Icon: InsightsOutlinedIcon,
        title: t("host_guide_v2.flow.analytics_promotion.title", { defaultValue: "Analytics & Promotion" }),
        text: t("host_guide_v2.flow.analytics_promotion.text", { defaultValue: "Track views and activity over time, and promote your event to reach more relevant participants." }),
      },
      {
        level: "main",
        Icon: PaymentsOutlinedIcon,
        title: t("host_guide_v2.flow.monetization.title", { defaultValue: "Monetization" }),
        text: t("host_guide_v2.flow.monetization.text", { defaultValue: "Become a verified VaultGrid host and turn your events into a paid format." }),
      },
      {
        level: "detail",
        Icon: SellOutlinedIcon,
        title: t("host_guide_v2.flow.ticket_sales.title", { defaultValue: "Verified Ticket Sales" }),
        text: t("host_guide_v2.flow.ticket_sales.text", { defaultValue: "Sell event access directly through VaultGrid without external payment links." }),
      },
      {
        level: "detail",
        Icon: QrCodeScannerOutlinedIcon,
        title: t("host_guide_v2.flow.attendance_control.title", { defaultValue: "Ticket & Attendance Control" }),
        text: t("host_guide_v2.flow.attendance_control.text", { defaultValue: "Confirm attendance only for participants with valid tickets using geolocation or QR check-in." }),
      },
      {
        level: "main",
        Icon: StarOutlineRoundedIcon,
        title: t("host_guide_v2.flow.feedback.title", { defaultValue: "Feedback" }),
        text: t("host_guide_v2.flow.feedback.text", { defaultValue: "After every event, collect ratings and comments to improve your format and strengthen trust." }),
      },
      {
        level: "detail",
        Icon: StarOutlineRoundedIcon,
        title: t("host_guide_v2.flow.required_rating.title", { defaultValue: "Required Event Rating" }),
        text: t("host_guide_v2.flow.required_rating.text", { defaultValue: "After the event, every participant is asked to rate the experience from 1 to 5." }),
      },
      {
        level: "detail",
        Icon: ForumOutlinedIcon,
        title: t("host_guide_v2.flow.anonymous_feedback.title", { defaultValue: "Anonymous Comments & Insights" }),
        text: t("host_guide_v2.flow.anonymous_feedback.text", { defaultValue: "Participants can leave anonymous written feedback to highlight what worked and what should improve." }),
      },
      {
        level: "detail",
        Icon: Groups2OutlinedIcon,
        title: t("host_guide_v2.flow.reputation_visibility.title", { defaultValue: "Host Reputation & Visibility" }),
        text: t("host_guide_v2.flow.reputation_visibility.text", { defaultValue: "Consistent quality and positive ratings improve your host reputation and event visibility." }),
      },
    ],
    [t]
  );

  const orbitNodes = useMemo(
    () =>
      modelLayout.nodes.map((node) => {
        const point = getOrbitPoint(node.orbitAngle + topOrbitRotation);
        return { ...node, ...point };
      }),
    [modelLayout, topOrbitRotation]
  );

  const chainItems = useMemo(() => {
    const containerWidth = Math.min(viewportWidth, 820) - (isDesktop ? 48 : 32);
    const textWidthPx = Math.max(180, (containerWidth * chainLayout.textWidth) / 100);
    const rowInnerPad = isDesktop ? 22 : 16;
    const titleLineHeight = 1.16;
    const bodyLineHeight = 1.32;

    let cursorTop = chainLayout.top;
    let mainNumber = 0;

    return chainFlow.map((item, index) => {
      const size = item.level === "main" ? chainLayout.mainSize : chainLayout.detailSize;
      const titleSize = item.level === "main" ? chainLayout.mainTitleSize : chainLayout.detailTitleSize;
      const textSize = item.level === "main" ? chainLayout.mainTextSize : chainLayout.detailTextSize;
      const titleLines = estimateWrappedLines(item.title, titleSize, textWidthPx);
      const bodyLines = estimateWrappedLines(item.text, textSize, textWidthPx);
      const titleHeight = titleLines * titleSize * titleLineHeight;
      const bodyHeight = bodyLines * textSize * bodyLineHeight;
      const textBlockHeight = titleHeight + 4 + bodyHeight;
      const rowHeight = Math.max(size, textBlockHeight) + rowInnerPad;

      const y = cursorTop + rowHeight / 2;
      const next = chainFlow[index + 1];
      const numberedMain = item.level === "main" ? ++mainNumber : null;

      if (next) {
        if (item.level === "main" && next.level === "detail") {
          cursorTop += rowHeight + chainLayout.mainToDetailGap;
        } else if (item.level === "detail" && next.level === "detail") {
          cursorTop += rowHeight + chainLayout.detailToDetailGap;
        } else if (item.level === "detail" && next.level === "main") {
          cursorTop += rowHeight + chainLayout.detailToMainGap;
        } else {
          cursorTop += rowHeight + chainLayout.mainToDetailGap;
        }
      } else {
        cursorTop += rowHeight;
      }

      return { ...item, size, y, rowHeight, mainNumber: numberedMain };
    });
  }, [chainFlow, chainLayout, isDesktop, viewportWidth]);

  const chainSectionHeight = useMemo(() => {
    if (!chainItems.length) return 1200;
    const last = chainItems[chainItems.length - 1];
    return Math.ceil(last.y + last.size / 2 + chainLayout.blockBottomPad);
  }, [chainItems, chainLayout]);

  const chainItemTimings = useMemo(() => {
    let cursor = 0;
    const base = chainFlow.map((item) => {
      const span = item.level === "main" ? CHAIN_ANIMATION.mainSpan : CHAIN_ANIMATION.detailSpan;
      const start = cursor;
      const end = start + span;
      cursor = end + (item.level === "main" ? CHAIN_ANIMATION.afterMainGap : CHAIN_ANIMATION.afterDetailGap);
      return { start, end };
    });

    const total = Math.max(cursor, 0.001);
    return base.map((timing) => ({
      start: timing.start / total,
      end: timing.end / total,
    }));
  }, [chainFlow]);

  useEffect(() => {
    let rafId = null;
    const section = chainSectionRef.current;
    const topSection = topSectionRef.current;
    if (!section || !topSection) return undefined;
    const scrollParent = findScrollParent(section);

    const updateProgress = () => {
      const topRect = topSection.getBoundingClientRect();
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight || 1;

      const topStartLine = vh * 0.23;
      const topEndLine = vh * -0.32;
      const topRange = Math.max(topStartLine - topEndLine, 1);
      const topRaw = (topStartLine - topRect.top) / topRange;
      const topNext = clamp(topRaw, 0, 1);
      const topSpinRange = Math.max(vh * 0.9, 1);
      const spinRaw = (topStartLine - topRect.top) / topSpinRange;
      const spinNext = clamp(spinRaw, 0, 7);

      setTopScrollProgress((prev) => (Math.abs(prev - topNext) < 0.001 ? prev : topNext));
      setTopSpinProgress((prev) => (Math.abs(prev - spinNext) < 0.001 ? prev : spinNext));

      const startLine = vh * chainLayout.revealStartRatio;
      const endLine = vh - chainLayout.revealBottomInsetPx;
      // Ensure progress reaches 1.0 while the content is still above bottom overlays.
      const range = Math.max(rect.height - (endLine - startLine), 1);
      const raw = (startLine - rect.top) / range;
      const next = clamp(raw, 0, 1);

      setChainProgress((prev) => (Math.abs(prev - next) < 0.001 ? prev : next));
    };

    const onScrollOrResize = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        updateProgress();
      });
    };

    updateProgress();
    if (scrollParent === window) {
      window.addEventListener("scroll", onScrollOrResize, { passive: true });
    } else {
      scrollParent.addEventListener("scroll", onScrollOrResize, { passive: true });
      // Parent container can move without native scroll events on window.
      window.addEventListener("scroll", onScrollOrResize, { passive: true });
    }
    window.addEventListener("resize", onScrollOrResize);

    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      if (scrollParent === window) {
        window.removeEventListener("scroll", onScrollOrResize);
      } else {
        scrollParent.removeEventListener("scroll", onScrollOrResize);
        window.removeEventListener("scroll", onScrollOrResize);
      }
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [chainLayout.revealBottomInsetPx, chainLayout.revealStartRatio]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth || 390);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const rootMinHeight = extraBottomSpaceCss
    ? "100dvh"
    : "calc(100dvh + var(--bottom-nav-height, 72px) + env(safe-area-inset-bottom, 0px))";

  return (
    <div
      className={`relative w-full overflow-x-hidden bg-[#07183f] text-white ${
        isStandaloneGuide
          ? "min-h-screen"
          : "min-h-full"
      }`}
      style={{
        minHeight: rootMinHeight,
        backgroundImage:
          isStandaloneGuide
            ? "linear-gradient(180deg,#3462c6 0%,#2a56b8 26%,#1f469f 52%,#12347f 78%,#12347f 100%)"
            : "linear-gradient(180deg,#3462c6 0%,#2a56b8 26%,#1f469f 52%,#12347f 78%,#0a245d 100%)",
        backgroundRepeat: "no-repeat",
        backgroundSize: "100% 100%",
      }}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_24%_4%,rgba(87,149,255,0.28),transparent_44%),radial-gradient(circle_at_83%_78%,rgba(26,127,238,0.16),transparent_40%)]" />
      {returnToCreatePath ? (
        <button
          type="button"
          onClick={returnToCreation}
          aria-label={t("event_create.step1.back_plain", { defaultValue: "Back to event creation" })}
          className="absolute left-4 top-[calc(var(--vg-safe-area-top)+20px)] z-40 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" className="h-6 w-6" fill="none">
            <path d="M15 6L9 12L15 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      ) : null}
      <style>{`
        @keyframes hostGuideSwipeFloat {
          0%,
          100% {
            transform: translateY(0);
            opacity: 0.72;
          }
          50% {
            transform: translateY(7px);
            opacity: 1;
          }
        }
      `}</style>

      <div
        className="relative mx-auto w-full max-w-[820px] px-4 sm:px-6"
        style={{
          paddingTop: "calc(var(--vg-safe-area-top) + 20px)",
          paddingBottom: contentBottomSpace,
        }}
      >
        <header className="text-center">
          <div
            className="mb-2 flex items-center justify-center gap-2"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            <img
              src={castleLogo}
              alt={t("brand.logo_alt", { ns: "communities", defaultValue: "Vaultgrid logo" })}
              className="shrink-0 opacity-95"
              style={{ width: "32px", height: "32px" }}
            />
            <span
              className="leading-none lowercase text-white"
              style={{
                fontSize: "18px",
                fontWeight: 700,
              }}
            >
              vaultgrid
              <span
                className="ml-[1px] inline-block font-semibold normal-case tracking-tight text-white"
                style={{
                  fontSize: "12px",
                  position: "relative",
                  top: "-11px",
                }}
              >
                βeta
              </span>
            </span>
          </div>

          <h1 className="text-[50px] font-semibold leading-none tracking-[-0.03em] sm:text-[66px]">
            {t("host_guide_v2.page_title", { defaultValue: "Host Guide" })}
          </h1>

          <p className="mt-4 text-[20px] leading-[1.28] text-[#b7c4e6] sm:text-[30px]">
            {t("host_guide_v2.hero_subtitle_line_1", {
              defaultValue: "All the tools you need.",
            })}
            <br />
            {t("host_guide_v2.hero_subtitle_line_2", {
              defaultValue: "One smooth experience.",
            })}
          </p>
        </header>

        <section ref={topSectionRef} className="relative mt-0 sm:mt-1" style={{ height: `${modelLayout.sectionHeight}px` }}>
          <div
            className="absolute -translate-x-1/2 -translate-y-1/2"
            style={{
              left: `${modelLayout.stageCenterX}%`,
              top: `${modelLayout.stageCenterY}%`,
              width: `${modelLayout.stageSize}px`,
            }}
          >
            <div
              className="relative aspect-square w-full"
              style={{ transformOrigin: "50% 50%" }}
            >
              <div className="absolute inset-0 rounded-full border border-dashed border-[#7ba7ff]/35" />

              <div
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-[#3572ff] bg-[radial-gradient(circle_at_30%_20%,rgba(79,147,255,0.28),rgba(2,13,37,0.95)_55%)] shadow-[0_0_42px_rgba(46,123,255,0.24),inset_0_0_24px_rgba(55,111,255,0.28)]"
                style={{
                  width: `${modelLayout.coreSize}px`,
                  height: `${modelLayout.coreSize}px`,
                }}
              >
                <div className="flex h-full flex-col items-center justify-center gap-0 px-3 text-center -translate-y-2 sm:-translate-y-3">
                  <img
                    src={castleLogo}
                    alt=""
                    className="opacity-95"
                    style={{
                      width: `${modelLayout.logoSize}px`,
                      height: `${modelLayout.logoSize}px`,
                    }}
                  />
                  <p className="mb-2 font-semibold leading-tight tracking-tight" style={{ fontSize: `${modelLayout.centerTextSize}px` }}>
                    {t("host_guide_v2.center.line_1", { defaultValue: "You Host." })}
                  </p>
                  <p className="mb-0.5 font-semibold leading-tight tracking-tight" style={{ fontSize: `${modelLayout.centerTextSize}px` }}>
                    {t("host_guide_v2.center.line_2", { defaultValue: "We Handle" })}
                  </p>
                  <p className="font-semibold leading-tight tracking-tight" style={{ fontSize: `${modelLayout.centerTextSize}px` }}>
                    {t("host_guide_v2.center.line_3", { defaultValue: "the Rest." })}
                  </p>
                </div>
              </div>

              {orbitNodes.map((node) => {
                const step = steps[node.step];
                const Icon = step.Icon;
                return (
                  <div key={step.title}>
                    <div
                      className="absolute -translate-x-1/2 -translate-y-1/2"
                      style={{
                        left: `${node.x}%`,
                        top: `${node.y}%`,
                      }}
                    >
                      <IconBubble Icon={Icon} size={modelLayout.nodeSize} />
                    </div>

                    <div
                      className={`absolute ${stepLabelAlignToClass(node.labelAlign)}`}
                      style={{
                        left: `${node.x}%`,
                        top: `${node.y}%`,
                        width: `${topLabelWidth}px`,
                        marginLeft: `${node.labelDx}px`,
                        marginTop: `${node.labelDy}px`,
                        opacity: 1 - topDissolve,
                        transform: `${stepLabelAlignToTransform(node.labelAlign)} translateY(${-topDissolve * 8}px)`,
                      }}
                    >
                      <p className="font-semibold leading-tight text-white" style={{ fontSize: `${modelLayout.labelTitleSize}px`, whiteSpace: "nowrap" }}>
                        {`${node.step + 1}. ${step.title}`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <div
          className="relative -mt-20 mb-0 flex flex-col items-center justify-center text-center sm:-mt-24 sm:mb-0"
          style={{ animation: "hostGuideSwipeFloat 1.9s ease-in-out infinite" }}
        >
          <p className="text-[12.5px] font-medium uppercase tracking-[0.13em] text-[#bfd2ff]/90 sm:text-[13.5px]">
            {t("host_guide_v2.swipe_hint", { defaultValue: "swipe down to learn more" })}
          </p>
          <span className="mt-1 text-[18px] leading-none text-[#bfd2ff]/90 sm:text-[20px]">↓</span>
        </div>

        <section ref={chainSectionRef} className="relative -mt-6 px-1 pb-4 sm:-mt-5" style={{ height: `${chainSectionHeight}px` }}>
          {chainItems.slice(0, -1).map((item, index) => {
            const next = chainItems[index + 1];
            const top = item.y + item.size / 2 + chainLayout.connectorGapPx;
            const bottom = next.y - next.size / 2 - chainLayout.connectorGapPx;
            const height = Math.max(bottom - top, 0);
            const currentTiming = chainItemTimings[index] || { start: 0, end: 1 };
            const nextTiming = chainItemTimings[index + 1] || { start: 0, end: 1 };
            const segStart = (currentTiming.start + currentTiming.end) / 2;
            const segEnd = nextTiming.end;
            const segRaw = (chainProgress - segStart) / Math.max(segEnd - segStart, 0.001);
            const segReveal = easeOutCubic(clamp(segRaw, 0, 1));

            return (
              <div
                key={`connector-${index}`}
                className="absolute origin-top border-l-2 border-dashed border-[#9fc1ff]/80 will-change-transform"
                style={{
                  left: `${chainLayout.railX}%`,
                  top: `${top}px`,
                  height: `${height}px`,
                  transform: `scaleY(${segReveal})`,
                  opacity: 0.2 + segReveal * 0.8,
                }}
              />
            );
          })}

          {chainItems.map((item, index) => {
            const titleSize = item.level === "main" ? chainLayout.mainTitleSize : chainLayout.detailTitleSize;
            const textSize = item.level === "main" ? chainLayout.mainTextSize : chainLayout.detailTextSize;
            const timing = chainItemTimings[index] || { start: 0, end: 1 };
            const raw = (chainProgress - timing.start) / Math.max(timing.end - timing.start, 0.001);
            const base = clamp(raw, 0, 1);
            const reveal = easeOutCubic(base);
            const pop = easeOutBack(base);
            const bubbleScaleBase = item.level === "main" ? 0.72 : 0.66;
            const bubbleScale = bubbleScaleBase + pop * (item.level === "main" ? 0.3 : 0.34);
            const bubbleShiftY = (1 - reveal) * (item.level === "main" ? 24 : 16);
            const bubbleShiftX = (1 - reveal) * 18;
            const textShiftY = (1 - reveal) * 18;
            const textShiftX = (1 - reveal) * -24;
            const drift = Math.sin(chainProgress * 10 + index * 0.5) * (item.level === "main" ? 2.4 : 1.3);
            const title = item.level === "main" ? `${item.mainNumber}. ${item.title}` : item.title;

            return (
              <div key={`${item.title}-${index}`}>
                <div
                  className="absolute will-change-transform"
                  style={{
                    left: `${chainLayout.railX}%`,
                    top: `${item.y + drift}px`,
                    opacity: reveal,
                    transform: `translate(calc(-50% + ${bubbleShiftX}px), calc(-50% + ${bubbleShiftY}px)) scale(${bubbleScale})`,
                    borderRadius: "9999px",
                  }}
                >
                  <IconBubble Icon={item.Icon} size={item.size} />
                </div>

                <div
                  className="absolute will-change-transform"
                  style={{
                    left: `${chainLayout.textX}%`,
                    top: `${item.y + drift * 0.45}px`,
                    width: `${chainLayout.textWidth}%`,
                    opacity: reveal,
                    transform: `translate(${textShiftX}px, calc(-50% + ${textShiftY}px))`,
                  }}
                >
                  <p className="font-semibold leading-tight text-white" style={{ fontSize: `${titleSize}px` }}>
                    {title}
                  </p>
                  <p className="mt-1 leading-[1.32] text-[#b7c4e6]" style={{ fontSize: `${textSize}px` }}>
                    {item.text}
                  </p>
                </div>
              </div>
            );
          })}
        </section>

        {isStandaloneGuide ? (
          <div className="sticky bottom-4 z-30 -mt-12 mb-8 flex w-full justify-center px-4 pb-1 sm:-mt-10 sm:mb-10 sm:px-6 lg:bottom-5">
            <button
              type="button"
              onClick={handleCreateEventCta}
              className="pointer-events-auto inline-flex min-h-[55px] min-w-[220px] items-center justify-center rounded-[100px] border border-[#6ea7ff]/75 bg-[#4278e8] px-8 py-3 text-base font-semibold text-white shadow-[0_12px_28px_rgba(46,112,238,0.34)] transition hover:bg-[#4c83f1] active:scale-[0.985]"
            >
              {t("host_guide_v2.cta_create_event", { ns: "events", defaultValue: "Create event" })}
            </button>
          </div>
        ) : null}

        <section className="relative z-10 mt-1 overflow-visible px-1 pb-1 sm:mt-3">
          <div
            className="pointer-events-none absolute z-0 w-[70%] max-w-[560px] opacity-80 sm:w-[62%] lg:w-[48%]"
            style={{
              right: `${clubLayout.visualRight}px`,
              top: `${clubLayout.visualTop}px`,
            }}
          >
            <div className="absolute inset-x-[14%] bottom-[8%] h-[34%] rounded-full bg-[#2f7dff]/22 blur-[52px]" />
            <img
              src="/assets/events/host_guide_club.png"
              alt=""
              className="relative z-10 w-full object-contain"
              loading="lazy"
            />
          </div>

          <div
            className="relative z-10 max-w-[64%] sm:max-w-[56%]"
            style={{ marginLeft: `${clubLayout.textLeft}%` }}
          >
            <h3 className="text-[24px] font-semibold leading-[1.08] tracking-[-0.02em] text-white sm:text-[32px] lg:text-[36px]">
              {t("host_guide_v2.clubs.title_line_1", { defaultValue: "Take It to" })}
              <br />
              {t("host_guide_v2.clubs.title_line_2", { defaultValue: "the Next Level" })}
            </h3>
            <p className="mt-2 text-[14px] leading-[1.34] text-[#b7c9ef] sm:mt-3 sm:text-[18px] lg:text-[20px]">
              {t("host_guide_v2.clubs.subtitle_prefix", { defaultValue: "Create your community with" })}{" "}
              <span className="inline-flex items-baseline whitespace-nowrap">
                <button
                  type="button"
                  onClick={() => navigate("/clubs")}
                  className="font-semibold text-white underline decoration-white/95 underline-offset-[3px]"
                >
                  {t("host_guide_v2.clubs.link_label", { defaultValue: "VaultGrid Clubs" })}
                </button>
                <span>.</span>
              </span>
            </p>
          </div>

          <div
            className="relative z-10 mt-[8.5rem] sm:mt-[10rem] lg:mt-[11.5rem]"
            style={{ marginLeft: `${clubLayout.textLeft}%` }}
          >
            {[
              {
                Icon: PublicOutlinedIcon,
                title: t("host_guide_v2.clubs.cards.community.title", { defaultValue: "Community" }),
                text: t("host_guide_v2.clubs.cards.community.text", { defaultValue: "Build a loyal audience in a permanent space you control." }),
              },
              {
                Icon: EventAvailableOutlinedIcon,
                title: t("host_guide_v2.clubs.cards.events.title", { defaultValue: "Events & Communication" }),
                text: t("host_guide_v2.clubs.cards.events.text", { defaultValue: "Create events, share updates and chat with your members." }),
              },
              {
                Icon: QueryStatsOutlinedIcon,
                title: t("host_guide_v2.clubs.cards.growth.title", { defaultValue: "Growth & Monetization" }),
                text: t("host_guide_v2.clubs.cards.growth.text", { defaultValue: "Track engagement, grow your club and monetize membership." }),
              },
            ].map(({ Icon, title, text }, index, arr) => (
              <div key={title} className={`relative flex items-start gap-3 sm:gap-4 ${index < arr.length - 1 ? "pb-8 sm:pb-9" : ""}`}>
                <div className="relative mt-0.5 flex h-[3.9rem] w-[3.9rem] shrink-0 items-center justify-center rounded-full border border-[#5c90ec]/45 bg-[radial-gradient(circle_at_30%_25%,rgba(85,145,255,0.34),rgba(6,20,53,0.92)_66%)] shadow-[0_0_14px_rgba(66,129,255,0.24)] sm:h-[4.3rem] sm:w-[4.3rem]">
                  <Icon className="h-6 w-6 text-[#51a3ff] sm:h-7 sm:w-7" />
                  {index < arr.length - 1 ? (
                    <span className="pointer-events-none absolute left-1/2 top-full mt-2 h-8 -translate-x-1/2 border-l-2 border-dashed border-[#7cb0ff]/55 sm:h-9" />
                  ) : null}
                </div>
                <div className="min-w-0 flex-1 pt-0.5">
                  <p className="text-[16px] font-semibold leading-[1.12] text-white sm:text-[22px]">{title}</p>
                  <p className="mt-1 max-w-[34rem] text-[13.5px] leading-[1.32] text-[#b7c9ef] sm:text-[17px]">{text}</p>
                  {index < arr.length - 1 ? <div className="mt-3 border-b border-[#7cb0ff]/34 sm:mt-4" /> : null}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

    </div>
  );
};

export default HostGuide;
